/**
 * Is this pair of coordinates a real, plottable location?
 *
 * `properties.lat` / `properties.lng` are `double precision NOT NULL`, so a
 * property that was never geocoded stores `0` / `0` rather than `NULL`. A
 * `!= null` or `Number.isFinite` check passes for those, and the map then
 * centres on "null island" in the Gulf of Guinea — which is how a Munich flat
 * ended up pinned in the Atlantic (COL-48 follow-up).
 *
 * Treat as unplottable:
 *  - non-finite values (`NaN` from `Number(undefined)`, `Infinity`)
 *  - exactly 0 / 0, the ungeocoded sentinel
 *  - anything outside the valid lat/lng range
 *
 * 0 / 0 is a legitimate point in the ocean, but we have no listings at sea, so
 * rejecting it costs nothing and catches every ungeocoded row.
 */
export function hasPlottableCoordinates(
  lat: number | null | undefined,
  lng: number | null | undefined
): boolean {
  if (lat == null || lng == null) return false;

  const latitude = Number(lat);
  const longitude = Number(lng);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;
  if (latitude === 0 && longitude === 0) return false;
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return false;

  return true;
}

/** The coordinate half of a property-edit form's state. */
export type CoordinateFormState = {
  /** Coordinates from the most recent address pick, null when none was made. */
  lat: number | null;
  lng: number | null;
  /**
   * True once the address text was edited by hand after a pick (or without
   * one), so whatever coordinates the row already holds no longer describe
   * the address being saved.
   */
  geocodeInvalidated: boolean;
};

/**
 * What a property-edit save should write for `lat`/`lng` — or `null` for
 * "leave the stored value alone".
 *
 * Three cases, in precedence order:
 *  - a suggestion was picked → save its coordinates;
 *  - the address was hand-edited after/without a pick → write the 0/0
 *    ungeocoded sentinel, so a stale pin never outlives the address it
 *    described;
 *  - nothing touched the address → don't send the columns at all, so an edit
 *    to an unrelated field can't disturb an existing geocode.
 *
 * Shared by the submit patch and the "this property will have no map" notice
 * in `PropertyEditSheet`, so the warning can never disagree with what the
 * save actually does (COL-48 follow-up).
 */
export function resolveCoordinatePatch(
  form: CoordinateFormState
): { lat: number; lng: number } | null {
  if (form.lat != null && form.lng != null) {
    return { lat: form.lat, lng: form.lng };
  }
  if (form.geocodeInvalidated) return { lat: 0, lng: 0 };
  return null;
}

/**
 * Which "this property has no map" notice the property edit sheet should show.
 *
 *  - `none`      — the row will be plottable after this save;
 *  - `willClear` — it is plottable now and this save writes the sentinel,
 *                  i.e. there is a map and it is about to go away;
 *  - `missing`   — it has no map either way, so nothing is being lost and
 *                  saying otherwise would misdescribe the save.
 *
 * Lives here rather than inline in the sheet so the choice can be tested
 * against the function the component actually calls.
 */
export function resolveCoordinateNotice(
  form: CoordinateFormState,
  stored: { lat: number | null; lng: number | null }
): "none" | "willClear" | "missing" {
  const next = resolveCoordinatePatch(form) ?? stored;
  if (hasPlottableCoordinates(next.lat, next.lng)) return "none";
  return form.geocodeInvalidated && hasPlottableCoordinates(stored.lat, stored.lng)
    ? "willClear"
    : "missing";
}
